import { useAuth } from '@agentnet/auth';
import { Grid, Typography, useTheme } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { cancelOrderApi, getOrderFileDetailsApi } from 'api/orderManagement/order-management-api';
import { useOrderInfo } from 'hooks/OrderInfoContext';
import useAsync from 'hooks/useAsync';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import EllipsisMenuIcon from 'ui-kit/components/ellipsismenuicon/EllipsisMenuIcon';
import { AgentNetConfirmationDialog } from 'ui-kit/components/modal/ConfirmationDialog';
import useGlobalMessages from 'ui-kit/components/notification/useGlobalMessages';
import useSnackBars from 'ui-kit/components/notification/useSnackbars';
import { AccountDetailRow } from './AccountInfoDetailRow';
import { DetailRow } from './DetailRow';
import { getKeyByValue, OrderStatus } from './enum';
import { numberValueFormatter } from './fileListConfig';
import OrderEditContainer from './OrderEditContainer';
import { OrderFileDetailsType, OrderType } from './types';

const useStyles = makeStyles((theme: Theme) => ({
  orderDetailsContainer: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
  },
  title: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    flexGrow: 1,
    alignItems: 'flex-start',
    paddingTop: '4px',
  },
  subtitle: {
    whiteSpace: 'nowrap',
    width: 93,
  },
  sectionTitle: {
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
  },
  divider: {
    margin: `${theme.spacing(2)}px 0`,
  },
  borderBottom: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

const OrderInfoPanel: React.FC = () => {
  const { addGlobalMsg } = useGlobalMessages();
  const { addSnackbarMessage } = useSnackBars();
  const noInfo = '-';
  const classes = useStyles();
  const theme = useTheme();
  const location = useLocation<{ order: OrderType }>();
  const orderData = location.state?.order;
  const { selectedFilesAmount, accountInfoList, setLastCancelledOrderId } = useOrderInfo();
  const [openDrawer, setOpenDrawer] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [canCancelOrder, setCanCancelOrder] = useState<boolean>(false);

  const orderId: number = orderData?.orderId || 0;
  const reportedNet = orderData?.reportedAmount || 0;
  const calculatedNet = selectedFilesAmount?.calculatedNet;
  const netDifference = parseFloat((calculatedNet - reportedNet).toFixed(2));
  const calculatedGross = selectedFilesAmount?.calculatedGross;
  const netDifferenceColor = netDifference !== 0 ? theme.palette.error.main : theme.palette.success.main;

  const { getAccessToken } = useAuth();

  const cancelOrder = async (): Promise<boolean> => {
    const token = await getAccessToken();
    const response = await cancelOrderApi(orderId, token);
    return response.result.orderCancelStatus;
  };

  const {
    execute: executeCancelOrder,
    status: executeCancelOrderStatus,
    value: executeCancelOrderValue,
    errors: executeCancelOrderErrors,
  } = useAsync<boolean>(cancelOrder, false);

  const getOrderFilesDetails = async (): Promise<OrderFileDetailsType> => {
    const token = await getAccessToken();
    const result = await getOrderFileDetailsApi(orderId, token);
    return result;
  };

  const {
    execute: executeGetOrderFilesDetails,
    status: executeGetOrderFilesDetailsStatus,
    value: getOrderFilesDetailsValue,
    errors: executeGetOrderFilesDetailsErrors,
  } = useAsync<OrderFileDetailsType>(getOrderFilesDetails, false);

  useEffect(() => {
    if (executeCancelOrderValue && executeCancelOrderStatus === 'success') {
      addSnackbarMessage({
        message: `Order ID ${orderId} Cancelled`,
        type: 'success',
      });
      // Updates the Select Files label to Cancelled and refresh
      setLastCancelledOrderId(orderId);
    }

    if (executeCancelOrderStatus === 'error') {
      if (executeCancelOrderErrors && executeCancelOrderErrors.length > 0) {
        executeCancelOrderErrors.map((err) => {
          addGlobalMsg({
            message: err,
            type: 'error',
          });
        });
      }
    }
  }, [executeCancelOrderStatus]);

  useEffect(() => {
    // This is to get the latest Order Status in MenuIcon
    executeGetOrderFilesDetails();
  }, [orderId, executeCancelOrderStatus]);

  useEffect(() => {
    if (executeGetOrderFilesDetailsStatus === 'success' && getOrderFilesDetailsValue) {
      const isOrderCancellable =
        // Enable for statuses except CLOSED/CANCELLED
        getOrderFilesDetailsValue.orderStatus !== getKeyByValue(OrderStatus, OrderStatus.Closed) &&
        getOrderFilesDetailsValue.orderStatus !== getKeyByValue(OrderStatus, OrderStatus.Cancelled) &&
        //Disable when any files exist in Order Queue
        getOrderFilesDetailsValue.closedFiles.length === 0 &&
        getOrderFilesDetailsValue.openFiles.length === 0;

      setCanCancelOrder(isOrderCancellable);
    }

    if (executeGetOrderFilesDetailsStatus === 'error') {
      executeGetOrderFilesDetailsErrors?.map((err) => {
        addGlobalMsg({
          message: err,
          type: 'error',
        });
      });
    }
  }, [executeGetOrderFilesDetailsStatus]);

  return (
    <div>
      <Grid
        container
        className={`${classes.orderDetailsContainer} ${accountInfoList.length > 0 ? classes.borderBottom : ''}`}
      >
        <Grid item sm={12}>
          {/* Order Info Title */}
          <div className={classes.title}>
            <Typography variant="h4" className={classes.subtitle} color="textSecondary">
              Order Info
            </Typography>
            <EllipsisMenuIcon
              buttonDataTestId="order-mgmt-select-files-button-order-info-menu"
              menuItems={[
                {
                  label: 'Edit Order Info',
                  onClick: () => setOpenDrawer(true),
                  dataTestId: 'order-mgmt-select-files-menu-item-edit-order-info',
                },
                {
                  label: 'Cancel Order',
                  onClick: canCancelOrder ? () => setCancelDialogOpen(true) : undefined,
                  disabled: !canCancelOrder,
                  dataTestId: 'order-mgmt-select-files-menu-item-cancel-order',
                },
              ]}
            />
          </div>
          <DetailRow label="Firm Name:" value={orderData?.firm?.firmName || noInfo} />
          <DetailRow
            label="Order Date:"
            value={orderData?.orderDate ? moment(orderData.orderDate).format('MM/DD/YYYY') : noInfo}
          />
          <DetailRow label="Lockbox Number:" value={orderData?.lockboxNumber?.name || noInfo} />
          <DetailRow label="Batch/Ticket Reference No:" value={orderData?.referenceNumber || noInfo} />
          <DetailRow label="No. of Checks:" value={orderData?.checkCount || 0} isLast />

          {/* Summary Title */}
          <div className={classes.title}>
            <Typography variant="h4" className={classes.subtitle} color="textSecondary">
              Summary
            </Typography>
          </div>

          <DetailRow label="Calculated Gross:" value={numberValueFormatter(calculatedGross, true)} isAmount />
          <DetailRow label="Reported Net:" value={numberValueFormatter(reportedNet, true)} isAmount />
          <DetailRow label="Calculated Net:" value={numberValueFormatter(calculatedNet, true)} isAmount />
          <DetailRow
            label="Net Difference:"
            value={numberValueFormatter(netDifference, true)}
            isAmount
            isEnd
            valueStyle={{ color: netDifferenceColor }}
            isLast
          />

          {accountInfoList.length > 0 && (
            <>
              {/* Account Info Title */}
              <div className={classes.sectionTitle}>
                <Typography variant="h4" className={classes.subtitle} color="textSecondary">
                  Account Info
                </Typography>
              </div>
              {accountInfoList.map((accountInfo, index) => (
                <AccountDetailRow
                  key={index}
                  accountNumber={accountInfo.accountNumber}
                  state={accountInfo.jurisdictionStateCode}
                  costCenter={accountInfo.costCenter}
                  split={accountInfo.contractualSplit}
                  underwriter={accountInfo.underwriterCode}
                  isNationalAgentFlag={accountInfo.isNationalAgentFlag}
                  agentLinxStatus={accountInfo.agentLinxStatus}
                />
              ))}
            </>
          )}
        </Grid>
      </Grid>

      {/* Cancel Order Confirmation Dialog */}
      <AgentNetConfirmationDialog
        disabled={executeCancelOrderStatus === 'pending'}
        onConfirm={async () => {
          await executeCancelOrder();
          setCancelDialogOpen(false);
        }}
        open={cancelDialogOpen}
        onDismissAction={() => {
          setCancelDialogOpen(false);
        }}
        dialogTitle="Confirm Cancellation"
        dialogBtnContent="Yes, Cancel Order"
        dialogText={`Once canceled, this order cannot be reopened, and all files will be removed from the Order Queue.`}
        qaAttrPrefix={'${dataQaPrefix}-Cancel-Order-Confirmation-'}
      />

      <OrderEditContainer {...{ openDrawer: openDrawer, setOpenDrawer: setOpenDrawer, orderData: orderData }} />
    </div>
  );
};

export default OrderInfoPanel;
